import { Button, Spinner, cn } from "@heroui/react";
import "@svar-ui/react-gantt/all.css";
import lodash from "lodash";
import "./task-types.css";

import {
	Chip,
	ErrorContent,
	OpenMode,
	OperationDrawer,
} from "@onesolution/components";
import {
	type AppFormApi,
	useDarkModeSSR,
	useLanguage,
} from "@onesolution/hooks";

import type { OperationDetailsTabs } from "@onesolution/components/OperationDrawer/OperationDetails.tsx";
import ScrollBarAdditional from "@onesolution/components/ScrollBarAdditional";
import OperationsFilters from "@onesolution/components/form/OperationsFilters.tsx";
import { DEFAULT_OPERATION_STATUS } from "@onesolution/constants/index.ts";
import { useOperationFilters } from "@onesolution/hooks/useOperationFilters.tsx";
import { getOperationPlanningBaseFromServer } from "@onesolution/serverFn/operations/getOperationPlanningBase";
import { getPlanningOperationSubitemFromServer } from "@onesolution/serverFn/operations/getPlanningOperationSubitem";
import {
	Gantt,
	type IApi,
	Tooltip,
	Willow,
	WillowDark,
} from "@svar-ui/react-gantt";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import EmptyContent from "../Operations/TableEmptyContent.tsx";
import PlanningEmptyContent from "./PlanningEmptyContent";
import TooltipContent from "./TooltipContent.tsx";

const scales = [
	{ unit: "day", step: 1, format: "d" },
	{ unit: "day", step: 7, format: "'Week' w" },
	{ unit: "month", step: 1, format: "MMMM yyy" },
	{ unit: "quarter", step: 1, format: "q" },
];

const today = new Date();

const todayLine = {
	end: today,
	start: today,
	id: 888,
	lazy: false,
	subIds: [],
	parent: 0,
	type: "milestone",
	text: "today",
	status: { label: "", color: "" },
};

const taskTypes = [
	{ id: "draft", label: "Draft" },
	{ id: "blocked", label: "Blocked" },
	{ id: "tobecategorized", label: "To be categorized" },
	{ id: "paused", label: "Paused" },
	{ id: "tobestarted", label: "To be started" },
	{ id: "canceleddeleted", label: "Canceled / Deleted" },
	{ id: "tobechecked", label: "To be checked" },
	{ id: "quoteneeded", label: "Quote needed" },
	{ id: "running", label: "Running" },
	{ id: "complete", label: "Complete" },
	{ id: "notstarted", label: "Not Started" },
	{ id: "waitingforcustomer", label: "Not Waiting For Customer" },
	{ id: "tocome", label: "To Come" },
	{ id: "done", label: "Done" },
	{ id: "cancelled", label: "Cancelled" },
];

function sameDate(d1: Date, d2: Date, noDay = false) {
	return d1.getFullYear() === d2.getFullYear() &&
		d1.getMonth() === d2.getMonth() &&
		noDay
		? true
		: d1.getDate() === d2.getDate();
}

function LightModeTheme(props: { children: React.ReactNode }) {
	const { isDarkMode } = useDarkModeSSR();

	if (isDarkMode) return <WillowDark>{props.children}</WillowDark>;

	return <Willow>{props.children}</Willow>;
}

export default function Planning() {
	const { t } = useLanguage();
	const [isLoading, setIsLoading] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(0);
	const [drawerState, setDrawerState] = useState({
		mode: OpenMode.close,
		defaultTab: "overview" as OperationDetailsTabs,
		selectedOpId: "",
	});
	const [api, setApi] = useState<IApi | undefined>(undefined);
	const { form, filters } = useOperationFilters("operations-list", {
		status: DEFAULT_OPERATION_STATUS,
	});
	const { data, isPending, isError, refetch, isFetching } =
		getOperationPlanningBaseFromServer.useQuery(filters);
	const getSubtasks = useServerFn(getPlanningOperationSubitemFromServer);

	if (isError)
		return <ErrorContent isFetching={isFetching} refetch={refetch} />;

	if (lodash.isEmpty(data) && !isPending) return <PlanningEmptyContent />;

	const zoomLevels = [
		{
			minCellWidth: 120,
			maxCellWidth: 120,
			scales: [
				{ unit: "year", step: 1, format: "yyyy" },
				{
					unit: "day",
					step: 1,
					format: "MMM d",
				},
				{ unit: "hour", step: 1, format: "HH:mm" },
			],
		},

		// WEEKLY
		{
			minCellWidth: 80,
			maxCellWidth: 140,
			scales: [
				{ unit: "year", step: 1, format: "yyyy" },
				{ unit: "day", step: 7, format: `MMMM '${t("basic.week")}' w` },
				{
					unit: "day",
					step: 1,
					format: "MMM d",
				},
			],
		},

		{
			minCellWidth: 150,
			maxCellWidth: 400,
			scales: [
				{ unit: "year", step: 1, format: "yyyy" },
				{ unit: "month", step: 1, format: "MMMM yyy" },
				{
					unit: "day",
					step: 1,
					format: "MMM d",
				},
			],
		},
		{
			minCellWidth: 250,
			maxCellWidth: 350,
			scales: [
				{ unit: "year", step: 1, format: "yyyy" },
				{ unit: "quarter", step: 1, format: "QQQQ" },
				{ unit: "month", step: 1, format: "MMMM yyy" },
			],
		},
	];

	const columns = useMemo(
		() => [
			{
				id: "text",
				header: t("name.operation"),
				flexgrow: 1,
			},
			{
				id: "status",
				header: "",
				width: 150,
				cell: ({
					row,
				}: { row: { status?: { color: string; label: string } } }) => (
					<Chip
						variant="dot"
						classNames={{ content: "" }}
						dotColor={row.status?.color}
					>
						{row.status?.label}
					</Chip>
				),
			},
		],
		[t],
	);
	const init = (api: IApi) => {
		setApi(api);
		api.on("request-data", async (ev) => {
			const concernedTask = api.getTask(ev.id);
			setIsLoading(true);
			const rawTasks = await getSubtasks({
				data: { depth: concernedTask.$level, itemIds: concernedTask.subIds! },
			});
			const tasks = rawTasks?.map((task) => ({
				...task,
				start: task.start ? new Date(task.start) : undefined,
				end: task.end ? new Date(task.end) : undefined,
				unscheduled: !task.end || !task.start,
				lazy: !!task.subIds,
				parent: ev.id,
			}));

			if (tasks) {
				api.exec("provide-data", {
					id: ev.id,
					data: {
						tasks,
						links: [],
					},
				});
			}
			setIsLoading(false);
		});

		api.on("select-task", async (ev) => {
			const concernedOp = data?.items.find((op) => op.id === ev.id);
			if (!concernedOp) return;

			setDrawerState({
				mode: OpenMode.details,
				selectedOpId: concernedOp.id.toString(),
				defaultTab: "overview",
			});
		});
	};

	function highlightTime(d: Date, u: string) {
		if (zoomLevel === 2)
			return u === "month" && sameDate(d, today, true) ? "bg-primary-200" : "";

		return u === "day" && sameDate(d, today) ? "bg-primary-200" : "";
	}

	const tasks = useMemo(
		() =>
			data?.items
				.map((item) => ({
					...item,
					start: new Date(item.start!),
					end: new Date(item.end!),
				}))
				.concat([todayLine]) || [],
		[data],
	);

	return (
		<div className="max-h-full flex flex-col">
			<div className="flex justify-between w-full mb-6">
				<div>
					<div className="flex flex-row gap-2 items-center">
						<h1 className="text-3xl font-bold">{t("name.planning")}</h1>
						<Chip isLoading={isFetching} variant="flat" />
					</div>
				</div>
			</div>

			<OperationsFilters noName form={form as AppFormApi} />

			<div className="size-full overflow-x-auto max-h-full ">
				<div
					className={cn(
						"my-custom-gantt max-w-screen-3xl mx-auto max-h-full relative",
						isLoading ? "cursor-progress!" : "",
					)}
				>
					<div className="flex my-4 gap-2">
						<Button
							onPress={() => {
								api?.exec("select-task", {
									id: 888,
									toggle: false,
									show: "x",
								});
							}}
							variant="solid"
							color="primary"
						>
							{t("basic.today")}
						</Button>
						{[
							t("basic.daily"),
							t("basic.weekly"),
							t("basic.monthly"),
							t("basic.quaterly"),
						].map((label: string, id) => (
							<Button
								// biome-ignore lint/suspicious/noArrayIndexKey: <no other way, label arent computed at first render>
								key={id}
								onPress={() => setZoomLevel(id)}
								className={cn(zoomLevel === id && "bg-background")}
								variant={zoomLevel === id ? "bordered" : "light"}
							>
								{label}
							</Button>
						))}
					</div>
					{!isPending &&
						(data?.items?.length > 0 ? (
							<LightModeTheme>
								<Tooltip api={api} content={TooltipContent}>
									<Gantt
										readonly={true}
										zoom={{
											level: zoomLevel,
											levels: zoomLevels,
										}}
										init={init}
										columns={columns}
										lengthUnit="month"
										tasks={tasks}
										taskTypes={taskTypes}
										highlightTime={highlightTime}
										scales={scales}
									/>
								</Tooltip>
							</LightModeTheme>
						) : (
							<EmptyContent />
						))}

					<div className="h-0 sticky bottom-0 overflow-visible">
						<ScrollBarAdditional
							key={zoomLevel}
							classToSync="wx-chart"
							className="absolute bottom-0 right-0"
						/>
					</div>
					{isLoading && (
						<div className="absolute top-2 left-2">
							<Spinner size="sm" />
						</div>
					)}
					{isPending && (
						<div className="flex justify-center items-center mt-8">
							<Spinner size="lg" color="primary" />
						</div>
					)}
				</div>
			</div>
			<OperationDrawer
				mode={drawerState.mode}
				onClose={() =>
					setDrawerState({
						...drawerState,
						mode: OpenMode.close,
						selectedOpId: "",
					})
				}
				onEdit={() => {
					setDrawerState({
						...drawerState,
						mode: OpenMode.edition,
					});
				}}
				selectedOpId={drawerState.selectedOpId}
			/>
		</div>
	);
}
